import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Container, Paper, Typography } from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { storeCommissionApi } from '../services/api';
import CommissionScheduleEditor, {
  draftFromSchedule,
  draftToPayload,
  validateDraft,
  describeSchedule,
} from './CommissionScheduleEditor';

/** Admin page for the platform DEFAULT store commission (Flat or volume tiers). */
const StoreCommission = () => {
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const schedule = await storeCommissionApi.getDefault();
      setSaved(schedule);
      setDraft(draftFromSchedule(schedule));
    } catch (err) {
      setLoadError(err.backendMessage || err.message || 'Failed to load the default commission');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const validity = draft ? validateDraft(draft, { flatRequired: true }) : { valid: false };

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    setSuccess('');
    try {
      const schedule = await storeCommissionApi.updateDefault(draftToPayload(draft));
      setSaved(schedule);
      setDraft(draftFromSchedule(schedule));
      setSuccess('Default commission saved');
    } catch (err) {
      // The server's 400 names the offending row/field; show it verbatim next to the form.
      setSaveError(err.backendMessage || err.message || 'Failed to save the default commission');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Typography variant="h5" component="h1" gutterBottom>Store commission</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        The platform fee taken from store orders. This default applies to every store unless a store
        has its own schedule (set on the store&apos;s provider page).
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress aria-label="Loading default commission" />
        </Box>
      )}

      {!loading && loadError && (
        <Alert
          severity="error"
          action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}
        >
          {loadError}
        </Alert>
      )}

      {!loading && draft && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
            Currently saved: {describeSchedule(saved)}
          </Typography>

          <CommissionScheduleEditor
            value={draft}
            onChange={(next) => { setDraft(next); setSuccess(''); }}
            flatRequired
            disabled={saving}
          />

          {saveError && <Alert severity="error" sx={{ mt: 2 }}>{saveError}</Alert>}
          {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}

          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={!validity.valid || saving}
            >
              Save
            </Button>
          </Box>
        </Paper>
      )}
    </Container>
  );
};

export default StoreCommission;
